import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  LoginRequest,
  LoginResponse,
  SolicitarOtpResponse,
  VerificarOtpRequest,
  User,
} from '../models/auth.model';
import { API_ENDPOINTS } from '../config/api.config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = API_ENDPOINTS.auth;

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private sessionRemainingSecondsSubject = new BehaviorSubject<number | null>(null);
  private sessionTimerId: ReturnType<typeof setInterval> | null = null;

  public currentUser$ = this.currentUserSubject.asObservable();
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  public token$ = this.tokenSubject.asObservable();
  public sessionRemainingSeconds$ = this.sessionRemainingSecondsSubject.asObservable();

  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    this.checkStoredAuth();
  }

  private checkStoredAuth(): void {
    const token = this.getStoredToken();
    const user = this.getStoredUser();

    if (token && user && this.isTokenValid(token)) {
      this.setAuthData(user, token);
    } else {
      this.clearAuthData();
    }
  }

  // Paso 1: Enviar credenciales y solicitar OTP
  solicitarOtp(credentials: LoginRequest): Observable<SolicitarOtpResponse> {
    return this.http
      .post<SolicitarOtpResponse>(
        `${this.apiUrl}/super-usuario/solicitar-otp`,
        credentials,
        this.httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  // Paso 2: Verificar OTP y obtener token
  verificarOtp(data: VerificarOtpRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(
        `${this.apiUrl}/super-usuario/verificar-otp`,
        data,
        this.httpOptions,
      )
      .pipe(
        tap((response) => {
          const token = this.extractToken(response);
          if (!response.user || !token) {
            throw new Error('Respuesta de login incompleta');
          }
          this.setAuthData(response.user, token);
        }),
        catchError(this.handleError),
      );
  }

  // Login legacy (sin 2FA, mantenido por compatibilidad)
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(
        `${this.apiUrl}/login-super-usuario`,
        credentials,
        this.httpOptions,
      )
      .pipe(
        tap((response) => {
          const token = this.extractToken(response);
          if (!response.user || !token) {
            throw new Error('Respuesta de login incompleta: faltan datos de autenticacion');
          }
          this.setAuthData(response.user, token);
        }),
        catchError(this.handleError),
      );
  }

  logout(): Observable<any> {
    const token = this.getStoredToken();
    const headers = token
      ? this.httpOptions.headers.set('Authorization', `Bearer ${token}`)
      : this.httpOptions.headers;

    return this.http.post(`${this.apiUrl}/logout`, {}, { headers }).pipe(
      tap(() => {
        this.clearAuthData();
        this.router.navigate(['/auth/login']);
      }),
      catchError((error) => {
        this.clearAuthData();
        this.router.navigate(['/auth/login']);
        return throwError(() => error);
      }),
    );
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  getToken(): string | null {
    return this.tokenSubject.value;
  }

  private setAuthData(user: User, token: string): void {
    const normalizedUser = this.normalizeUser(user);

    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(normalizedUser));

    this.currentUserSubject.next(normalizedUser);
    this.tokenSubject.next(token);
    this.isAuthenticatedSubject.next(true);

    this.startSessionTimer(token);
  }

  private clearAuthData(): void {
    this.stopSessionTimer();

    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_refresh_token');

    this.currentUserSubject.next(null);
    this.tokenSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    this.sessionRemainingSecondsSubject.next(null);
  }

  private getStoredToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private getStoredUser(): User | null {
    const userStr = localStorage.getItem('auth_user');
    return userStr ? JSON.parse(userStr) : null;
  }

  private isTokenValid(token: string): boolean {
    if (!token) return false;

    const payload = this.decodeJwtPayload(token);
    if (!payload) return true;

    const now = Math.floor(Date.now() / 1000);
    return Number(payload.exp || 0) > now;
  }

  private startSessionTimer(token: string): void {
    this.stopSessionTimer();

    const payload = this.decodeJwtPayload(token);
    const exp = Number(payload?.exp || 0);

    if (!exp) {
      this.sessionRemainingSecondsSubject.next(null);
      return;
    }

    const tick = (): void => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = Math.max(0, exp - now);
      this.sessionRemainingSecondsSubject.next(remaining);

      if (remaining <= 0) {
        this.clearAuthData();
        this.router.navigate(['/auth/login'], {
          queryParams: { expired: '1' },
        });
      }
    };

    tick();
    this.sessionTimerId = setInterval(tick, 1000);
  }

  private stopSessionTimer(): void {
    if (this.sessionTimerId) {
      clearInterval(this.sessionTimerId);
      this.sessionTimerId = null;
    }
  }

  private decodeJwtPayload(token: string): any | null {
    if (!token.includes('.')) return null;

    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  }

  private extractToken(response: LoginResponse): string | null {
    const responseAny = response as any;
    return (
      response.accessToken ??
      responseAny?.access_token ??
      responseAny?.token ??
      responseAny?.data?.accessToken ??
      responseAny?.data?.access_token ??
      responseAny?.data?.token ??
      null
    );
  }

  private normalizeUser(user: User): User {
    const userAny = user as any;
    return {
      ...user,
      nombreCompleto: userAny.nombreCompleto ?? userAny.nombre_completo ?? '',
      empresa_id: userAny.empresa_id ?? userAny.empresaId,
      created_at: userAny.created_at ?? userAny.createdAt,
      updated_at: userAny.updated_at ?? userAny.updatedAt,
      deleted_at: userAny.deleted_at ?? userAny.deletedAt,
    };
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'Ocurrió un error desconocido';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      if (error.status === 401) {
        errorMessage = 'Credenciales incorrectas';
      } else if (error.status === 403) {
        errorMessage = 'Acceso denegado';
      } else if (error.status === 404) {
        errorMessage = 'Usuario no encontrado';
      } else if (error.status === 422) {
        errorMessage = 'Datos de registro inválidos';
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error.error && error.error.message) {
        errorMessage = error.error.message;
      } else {
        errorMessage = `Error del servidor: ${error.status}`;
      }
    }

    console.error('Error en AuthService:', error);
    return throwError(() => new Error(errorMessage));
  }
}
